import React, { useState, useMemo } from "react";
import { toast } from "react-toastify";
import { Box, MenuItem } from "@mui/material";
import { C } from "../constants";
import { IPD_COLORS, ipdCardSx } from "../../ipd/utils/ipdColors";
import IpdTextField from "../../ipd/utils/IpdTextField";
import IpdDateField from "../../ipd/utils/IpdDateField";
import IpdFormField from "../../ipd/utils/IpdFormField";
import IpdButton from "../../ipd/utils/IpdButton";
import SvgIcon from "../SvgIcon";
import { toBillingError } from "../billing-api";
import { createService, updateService, deleteService } from "../service-master-api";

const UNITS = ["STUDY", "TEST", "VISIT", "DAY", "HOUR", "UNIT", "PROCEDURE", "SESSION"];

const todayISO = () => new Date().toISOString().slice(0, 10);

// Full-page Add/Edit Service screen — swapped in place of the Service Master
// grid (not an overlay modal), with its own back link to return to the grid.
export default function ServiceMasterServiceScreen({ mode, service, lookups, onBack, onSaved }) {
  const isEdit = mode === "edit" && service;

  const [form, setForm] = useState(() => ({
    name: service?.name || "",
    unique_code: service?.uniqueCode || "",
    group_id: service?.groupId != null ? String(service.groupId) : "",
    classification_id: service?.classificationId != null ? String(service.classificationId) : "",
    fee: service?.fee != null ? String(service.fee) : "",
    bed_type_code: service?.bedTypeCode || "",
    sample_id: service?.sampleId != null ? String(service.sampleId) : "",
    service_type: service?.serviceType || "",
    unit: service?.unit || "",
    charge_modifiable: service?.chargeModifiable ?? false,
    quantity_modifiable: service?.quantityModifiable ?? false,
    description: service?.description || "",
    cpt_code: service?.cptCode || "",
    // Validity window. Existing services created before this field may have no
    // effective_from — fall back to today so the mandatory field is never blank.
    effective_from: service?.effectiveFrom || todayISO(),
    effective_to: service?.effectiveTo || ""
  }));
  const [saving, setSaving] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [error, setError] = useState("");
  const [confirmRateOverride, setConfirmRateOverride] = useState(false);

  // Only relevant when editing: has the user actually typed a different fee?
  // Comparing against the service's stored fee (not just "is the field non-empty")
  // so editing an unrelated field (name, description, …) on a rate-locked service
  // doesn't accidentally try to change the fee and trip the backend's guard.
  const feeChanged = isEdit && Number(form.fee) !== Number(service?.fee ?? NaN);
  const needsRateOverride = isEdit && feeChanged && !service?.chargeModifiable;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setChecked = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.checked }));
  const setDate = (k) => (iso) => setForm((f) => ({ ...f, [k]: iso || "" }));

  const visibleClassifications = useMemo(
    () => (form.group_id ? lookups.classifications.filter((c) => String(c.groupId) === String(form.group_id)) : lookups.classifications),
    [lookups.classifications, form.group_id]
  );

  const onGroupChange = (e) => {
    const groupId = e.target.value;
    setForm((f) => {
      const cls = lookups.classifications.find((c) => String(c.id) === String(f.classification_id));
      const stillValid = cls && String(cls.groupId) === String(groupId);
      return { ...f, group_id: groupId, classification_id: stillValid ? f.classification_id : "" };
    });
  };

  const submit = async () => {
    if (!form.name.trim()) { setError("Name is required"); return; }
    if (!form.group_id) { setError("Group is required"); return; }
    if (!form.classification_id) { setError("Classification is required"); return; }
    if (form.fee.trim() === "" || Number.isNaN(Number(form.fee))) { setError("Enter a valid fee"); return; }
    if (!form.effective_from) { setError("Effective from is required"); return; }
    if (form.effective_to && form.effective_to < form.effective_from) {
      setError("Effective to cannot be earlier than Effective from");
      return;
    }
    if (needsRateOverride && !confirmRateOverride) {
      setError("This service's rate is locked. Check the override box below to confirm changing the fee anyway.");
      return;
    }

    setSaving(true); setError("");
    const body = {
      name: form.name.trim(),
      group_id: form.group_id,
      classification_id: form.classification_id,
      unit: form.unit || undefined,
      charge_modifiable: form.charge_modifiable,
      quantity_modifiable: form.quantity_modifiable,
      description: form.description.trim() || undefined,
      effective_from: form.effective_from,
      // null (not undefined) so clearing the end date on an edit actually clears it
      effective_to: form.effective_to || null,
    };
    // Only send `fee` when it's actually changing (always on create) — sending
    // it unchanged on every edit trips the backend's charge_modifiable guard
    // even for edits that have nothing to do with the rate.
    if (!isEdit || feeChanged) {
      body.fee = form.fee;
      if (needsRateOverride) body.force_rate_edit = true;
    }
    if (form.unique_code.trim()) body.unique_code = form.unique_code.trim().toUpperCase();
    if (form.cpt_code.trim()) body.cpt_code = form.cpt_code.trim().toUpperCase();

    if (form.bed_type_code) body.bed_type_code = form.bed_type_code;
    if (form.sample_id) body.sample_id = form.sample_id;

    try {
      if (isEdit) {
        await updateService(service.id, body);
        toast.success(`${form.unique_code || form.name} updated`);
      } else {
        await createService(body);
        toast.success(`${form.unique_code || form.name} created`);
      }
      await onSaved();
    } catch (err) {
      setError(toBillingError(err, "Could not save service"));
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!window.confirm(`Deactivate "${service.uniqueCode || service.name}"?`)) return;
    setDeactivating(true);
    try {
      await deleteService(service.id);
      toast.success(`${service.uniqueCode || service.name} deactivated`);
      await onSaved();
    } catch (err) {
      toast.error(toBillingError(err, "Could not deactivate service"));
      setDeactivating(false);
    }
  };

  return (
    <div style={{ animation: "ipdFadeIn 0.2s ease" }}>
      <style>{`@keyframes ipdFadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }`}</style>

      <IpdButton variant="outlined" onClick={onBack} startIcon={<SvgIcon name="back" size={16} color={C.text} />} sx={{ mb: "18px" }}>
        Back to Service Master
      </IpdButton>

      <Box sx={{ ...ipdCardSx }}>
        <div style={{ padding: "18px 22px", borderBottom: `1px solid ${C.border}` }}>
          <span style={{ fontSize: "17px", fontWeight: 800, color: C.text }}>{isEdit ? "Edit service" : "Add service"}</span>
        </div>

        <div style={{ padding: "22px", display: "flex", flexDirection: "column", gap: "18px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "16px" }}>
            <IpdFormField label="Name" required>
              <IpdTextField fullWidth value={form.name} onChange={set("name")} placeholder="Abdomen CT" />
            </IpdFormField>
            <IpdFormField label="Unique code">
              <IpdTextField fullWidth value={form.unique_code} onChange={set("unique_code")} placeholder="MHG-ABD-CT (auto-generated if blank)" sx={{ "& input": { fontFamily: "monospace" } }} />
            </IpdFormField>
            <IpdFormField label="CPT code">
              <IpdTextField fullWidth value={form.cpt_code} onChange={set("cpt_code")} placeholder="" sx={{ "& input": { fontFamily: "monospace" } }} />
            </IpdFormField>
          </div>
          <div style={{ fontSize: "11px", color: C.textSub, marginTop: "-10px" }}>
            Short meaningful unique code (org + clinical token), e.g. <b>MHG-CBC</b>. Never a bare number.
            Reuse the same code across bed variants of this service.
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "16px" }}>
            <IpdFormField label="Group" required>
              <IpdTextField select fullWidth value={form.group_id} onChange={onGroupChange}>
                <MenuItem value="">Select group…</MenuItem>
                {lookups.groups.map((g) => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
              </IpdTextField>
            </IpdFormField>
            <IpdFormField label="Classification" required>
              <IpdTextField select fullWidth value={form.classification_id} onChange={set("classification_id")} disabled={!form.group_id}>
                <MenuItem value="">{form.group_id ? "Select…" : "Select group first"}</MenuItem>
                {visibleClassifications.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </IpdTextField>
            </IpdFormField>
            <IpdFormField label="Fee (₹)" required>
              <IpdTextField fullWidth type="number" value={form.fee} onChange={set("fee")} placeholder="4500.00" />
            </IpdFormField>
            <IpdFormField label="Bed type">
              <IpdTextField select fullWidth value={form.bed_type_code} onChange={set("bed_type_code")}>
                <MenuItem value="">None</MenuItem>
                {lookups.bedTypes.map((b) => <MenuItem key={b.id} value={b.code}>{b.name} ({b.code})</MenuItem>)}
              </IpdTextField>
            </IpdFormField>
            <IpdFormField label="Sample type (labs)">
              <IpdTextField select fullWidth value={form.sample_id} onChange={set("sample_id")}>
                <MenuItem value="">None</MenuItem>
                {lookups.sampleTypes.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
              </IpdTextField>
            </IpdFormField>
            <IpdFormField label="Unit">
              <IpdTextField select fullWidth value={form.unit} onChange={set("unit")}>
                <MenuItem value="">—</MenuItem>
                {UNITS.map((u) => <MenuItem key={u} value={u}>{u}</MenuItem>)}
              </IpdTextField>
            </IpdFormField>
            <IpdFormField label="Effective from" required>
              <IpdDateField fullWidth value={form.effective_from} onChange={setDate("effective_from")} />
            </IpdFormField>
            <IpdFormField label="Effective to (optional)">
              <IpdDateField fullWidth value={form.effective_to} onChange={setDate("effective_to")} />
            </IpdFormField>
          </div>
          <div style={{ fontSize: "11px", color: C.textSub, marginTop: "-10px" }}>
            Effective to is optional — leave it blank for a service that stays valid indefinitely.
          </div>

          {needsRateOverride && (
            <div style={{ padding: "12px 14px", background: "#fff8e1", border: "1px solid #ffe69c", borderRadius: "8px" }}>
              <label style={{ display: "flex", alignItems: "flex-start", gap: "8px", fontSize: "13px", color: C.text, cursor: "pointer" }}>
                <input type="checkbox" checked={confirmRateOverride} onChange={(e) => setConfirmRateOverride(e.target.checked)} style={{ marginTop: "2px" }} />
                <span>
                  This service's rate is locked (<b>Rate modifiable</b> is off below). Confirm the admin override to
                  save this new fee anyway — or check "Rate modifiable" instead to unlock it going forward.
                </span>
              </label>
            </div>
          )}

          <IpdFormField label="Description">
            <IpdTextField fullWidth multiline rows={3} value={form.description} onChange={set("description")} />
          </IpdFormField>

          <div style={{ display: "flex", flexDirection: "column", gap: "8px", padding: "12px 14px", background: IPD_COLORS.pageBg, border: `1px solid ${C.border}`, borderRadius: "8px" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: C.text, cursor: "pointer" }}>
              <input type="checkbox" checked={form.charge_modifiable} onChange={setChecked("charge_modifiable")} />
              Rate modifiable at billing time
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: C.text, cursor: "pointer" }}>
              <input type="checkbox" checked={form.quantity_modifiable} onChange={setChecked("quantity_modifiable")} />
              Quantity modifiable at billing time
            </label>
          </div>

          {error && (
            <div style={{ padding: "10px 14px", background: IPD_COLORS.error.bg, border: `1px solid ${IPD_COLORS.error.border}`, borderRadius: "8px", color: IPD_COLORS.error.text, fontSize: "12px", fontWeight: 600 }}>
              {error}
            </div>
          )}
        </div>

        <div style={{ padding: "16px 22px", borderTop: `1px solid ${C.border}`, display: "flex", gap: "10px", alignItems: "center" }}>
          {isEdit && (
            <IpdButton
              variant="outlined"
              onClick={handleDeactivate}
              loading={deactivating}
              startIcon={<SvgIcon name="trash" size={14} color={IPD_COLORS.error.text} />}
              sx={{ color: IPD_COLORS.error.text, borderColor: IPD_COLORS.error.border, '&:hover': { borderColor: IPD_COLORS.error.text, backgroundColor: IPD_COLORS.error.bg } }}
            >
              Deactivate
            </IpdButton>
          )}
          <div style={{ flex: 1 }} />
          <IpdButton variant="outlined" onClick={onBack}>Cancel</IpdButton>
          <IpdButton onClick={submit} loading={saving}>
            {isEdit ? "Save changes" : "Create service"}
          </IpdButton>
        </div>
      </Box>
    </div>
  );
}
